import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Alert,
    Box,
    Typography,
    Paper,
    Stack,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';

interface TwoFASetupDialogProps {
    open: boolean;
    onClose: () => void;
    secret: string;
    qrCode: string;
    onConfirm: (otpCode: string) => Promise<boolean>;
    loading: boolean;
}

const TwoFASetupDialog: React.FC<TwoFASetupDialogProps> = ({
    open,
    onClose,
    secret,
    qrCode,
    onConfirm,
    loading,
}) => {
    const [otpCode, setOtpCode] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleConfirm = async () => {
        if (otpCode.length !== 6) {
            setError('Please enter a 6-digit code');
            return;
        }

        setError('');
        const result = await onConfirm(otpCode);
        if (result) {
            setSuccess(true);
            setTimeout(() => {
                setOtpCode('');
                setSuccess(false);
                onClose();
            }, 1500);
        } else {
            setError('Invalid code. Please try again.');
        }
    };

    const handleClose = () => {
        setOtpCode('');
        setError('');
        setSuccess(false);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
            <DialogTitle>Enable Two-Factor Authentication</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ pt: 2 }}>
                    {/* Instructions */}
                    <Box>
                        <Typography variant='body2' color='text.secondary' gutterBottom>
                            Use an authenticator app (Google Authenticator, 1Password, Authy, etc.) to scan this QR code:
                        </Typography>
                    </Box>

                    {/* QR Code Display */}
                    <Paper
                        elevation={0}
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            p: 2,
                            backgroundColor: '#f5f5f5',
                            borderRadius: 1,
                        }}
                    >
                        <div style={{ width: 200, height: 200 }}>
                            <QRCodeSVG
                                value={qrCode}
                                size={200}
                                level='H'
                                includeMargin={true}
                            />
                        </div>
                    </Paper>

                    {/* Manual Entry Backup */}
                    <Box>
                        <Typography variant='caption' color='text.secondary' display='block' gutterBottom>
                            Or enter this code manually:
                        </Typography>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 1.5,
                                backgroundColor: '#f5f5f5',
                                fontFamily: 'monospace',
                                fontSize: '14px',
                                wordBreak: 'break-all',
                                userSelect: 'all',
                            }}
                        >
                            {secret}
                        </Paper>
                    </Box>

                    {/* Status Messages */}
                    {error && <Alert severity='error'>{error}</Alert>}
                    {success && <Alert severity='success'>✓ 2FA enabled successfully!</Alert>}

                    {/* OTP Code Input */}
                    {!success && (
                        <TextField
                            label='6-Digit Code'
                            placeholder='000000'
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            inputProps={{ maxLength: 6 }}
                            fullWidth
                            disabled={loading}
                            helperText='Enter the 6-digit code from your authenticator app'
                        />
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={handleClose} disabled={loading || success}>
                    {success ? 'Done' : 'Cancel'}
                </Button>
                {!success && (
                    <Button
                        onClick={handleConfirm}
                        variant='contained'
                        disabled={loading || otpCode.length !== 6}
                    >
                        {loading ? 'Verifying...' : 'Verify & Enable'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default TwoFASetupDialog;
